import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Drawer, Button, Dropdown, Space } from 'antd';
import { MenuOutlined, GlobalOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { navigationData } from '@/data/navigation';
import type { Locale } from '@/locales';
import styles from './AppHeader.module.scss';
import logoImage from '@/assets/logo.png';

const { Header } = Layout;

export const AppHeader: React.FC = () => {
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // 监听滚动实现透明效果
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          // 滚动超过50px时，header变为透明
          setScrolled(scrollTop > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    if (currentPath === '/') return ['home'];
    
    // 检查是否匹配子菜单
    for (const item of navigationData) {
      if (item.children) {
        for (const child of item.children) {
          if (child.path === currentPath || currentPath.startsWith(child.path + '/')) {
            return [child.path];
          }
        }
      }
    }
    
    // 检查主菜单
    const matchedItem = navigationData.find(item => {
      if (item.path === '/') return false;
      return currentPath.startsWith(item.path);
    });
    
    return matchedItem ? [matchedItem.path] : [];
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'home') {
      navigate('/');
    } else {
      navigate(key);
    }
    setDrawerOpen(false);
  };

  const menuItems = navigationData.map(item => ({
    key: item.path === '/' ? 'home' : item.path,
    label: t(item.label),
    children: item.children?.map(child => ({
      key: child.path,
      label: t(child.label),
    })),
  }));

  const languageOptions = [
    { key: 'zh-CN', label: '简体中文' },
    { key: 'zh-TW', label: '繁體中文' },
    { key: 'en', label: 'English' },
  ];

  const languageMenuItems = languageOptions.map(lang => ({
    key: lang.key,
    label: lang.label,
    onClick: () => changeLanguage(lang.key as Locale),
  }));

  return (
    <Header className={`${styles.header} ${scrolled ? styles.transparent : styles.solid}`}>
      <div className={styles.container}>
        <Link to="/" className={styles.logo}>
          <img src={logoImage} alt="创美药业" />
        </Link>

        <div className={styles.menuWrapper}>
          <Menu
            mode="horizontal"
            selectedKeys={getSelectedKeys()}
            items={menuItems}
            onClick={handleMenuClick}
            className={styles.menu}
            overflowedIndicator={null}
            disabledOverflow
          />
        </div>

        <Space size="middle" className={styles.actions}>
          <Dropdown
            menu={{ items: languageMenuItems }}
            placement="bottomRight"
          >
            <Button type="text" icon={<GlobalOutlined />}>
              {languageOptions.find(l => l.key === currentLanguage)?.label}
            </Button>
          </Dropdown>

          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            className={styles.mobileMenuBtn}
          />
        </Space>

        <Drawer
          title={t('nav.menu', '导航菜单')}
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={280}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Menu
              mode="vertical"
              selectedKeys={getSelectedKeys()}
              items={menuItems}
              onClick={handleMenuClick}
            />
            
            <Dropdown
              menu={{ items: languageMenuItems }}
              placement="bottomLeft"
            >
              <Button icon={<GlobalOutlined />} block>
                {languageOptions.find(l => l.key === currentLanguage)?.label}
              </Button>
            </Dropdown>
          </Space>
        </Drawer>
      </div>
    </Header>
  );
};
